// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { sortedIndex, difference } from 'lodash';

// Utils
import rIC from '../../common/utils/lib/rIC';
import { asObject, getMany, prop } from '../../common/utils/immutableHelper';
import { getWindowHeight, getWindowWidth } from '../utils/domUtil';
import { getXPosition, getYPosition } from '../../common/elements/utils/elementPropertyUtils';
import { hasChanged } from '../utils/react/propsComparisons';
import { getCanvasReturnState } from './canvasReturnStateService';

// Selectors
import { getElements } from '../element/selectors/elementsSelector';
import { getCurrentVisibleBoardCanvasOrigin, getCurrentBoardId } from '../element/selectors/currentBoardSelector';

// Components
import CanvasElement from './CanvasElement';

// Analytics
import * as analyticsTimingService from '../analytics/timingService/analyticsTimingService';
import { NewRelicPageActions } from '../analytics/newRelicUtils';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../common/elements/elementConstants';

const MAX_ITERATIONS = 40;
const EMPTY_ARRAY = [];

const canvasElementIdsHaveChanged = hasChanged('canvasElementIds');

/**
 * Adds the specified element ID into the initialRenderElementIdsSet and removes it from the
 * orderedElementIds array if it's in there.
 *
 * NOTE: This uses mutations of the arguments to improve performance.
 */
const addInitialRenderElementId = (elementId, initialRenderElementIdsSet, orderedElementIds) => {
    if (!elementId || !orderedElementIds) return;

    initialRenderElementIdsSet.add(elementId);

    const index = orderedElementIds.indexOf(elementId);

    if (index < 0) return;

    orderedElementIds.splice(index, 1);
};

/**
 * Determines the order that elements should be rendered in based on their positions on the canvas
 * and the current scroll position.
 *
 * Elements that are currently within the viewport should be rendered before elements outside of
 * the viewport.
 */
const calculateElementOrder = (props, store) => {
    if (!props.canvasElementIds || !props.canvasElementIds.length) {
        return {
            initialRenderElementIds: EMPTY_ARRAY,
            orderedElementIds: EMPTY_ARRAY,
        };
    }

    const gridWidth = getWindowWidth() / props.gridSize;
    const gridHeight = getWindowHeight() / props.gridSize;

    const state = store.getState();

    const currentBoardId = getCurrentBoardId(state);
    const canvasReturnState = getCanvasReturnState(currentBoardId);

    const currentBoardCanvasOrigin = asObject(getCurrentVisibleBoardCanvasOrigin(state));

    const scrollLeft = canvasReturnState ? prop('scrollLeft', canvasReturnState) || 0 : 0;
    const scrollTop = canvasReturnState ? prop('scrollTop', canvasReturnState) || 0 : 0;

    const viewportStartX = currentBoardCanvasOrigin.x + scrollLeft;
    const viewportStartY = currentBoardCanvasOrigin.y + scrollTop;

    const viewportEndX = viewportStartX + gridWidth;
    const viewportEndY = viewportStartY + gridHeight;

    const viewportCenterX = (viewportEndX + viewportStartX) / 2;
    const viewportCenterY = (viewportEndY + viewportStartY) / 2;

    const renderStartX = viewportStartX - ELEMENT_DEFAULT_WIDTH;
    const renderStartY = viewportStartY - ELEMENT_DEFAULT_WIDTH;

    const elements = getElements(state);
    const canvasElements = getMany(props.canvasElementIds, elements);

    // Keeps an ordered array of ids so we can use binary search for insertions
    const orderedElementIds = [];
    const initialRenderElementIdsSet = new Set();
    const orderedDistances = [];

    let maxX = -Infinity;
    let maxXId = null;
    let minX = Infinity;
    let minXId = null;

    let maxY = -Infinity;
    let maxYId = null;
    let minY = Infinity;
    let minYId = null;

    canvasElements.forEach((element, elementId) => {
        const x = getXPosition(element);
        const y = getYPosition(element);

        if (x < minX) {
            minX = x;
            minXId = elementId;
        }

        if (x > maxX) {
            maxX = x;
            maxXId = elementId;
        }

        if (y < minY) {
            minY = y;
            minYId = elementId;
        }

        if (y > maxY) {
            maxY = y;
            maxYId = elementId;
        }

        // If the element is within the grid width and height we render it immediately
        if (x >= renderStartX && x < viewportEndX && y >= renderStartY && y < viewportEndY) {
            initialRenderElementIdsSet.add(elementId);
        } else {
            // Otherwise we render it based on the distance away from the center point of the viewport
            const xOffset = x - viewportCenterX;
            const yOffset = y - viewportCenterY;

            const distanceSqd = xOffset * xOffset + yOffset * yOffset;

            const insertionIndex = sortedIndex(orderedDistances, distanceSqd);

            orderedDistances.splice(insertionIndex, 0, distanceSqd);
            orderedElementIds.splice(insertionIndex, 0, elementId);
        }
    });

    addInitialRenderElementId(minXId, initialRenderElementIdsSet, orderedElementIds);
    addInitialRenderElementId(maxXId, initialRenderElementIdsSet, orderedElementIds);
    addInitialRenderElementId(minYId, initialRenderElementIdsSet, orderedElementIds);
    addInitialRenderElementId(maxYId, initialRenderElementIdsSet, orderedElementIds);

    return {
        initialRenderElementIds: [...initialRenderElementIdsSet],
        orderedElementIds,
    };
};

// Track if the board was already fetched before navigating to it
// TODO - I needed to put the variable outside of the class because the CanvasElementOrchestrator appears to
//  get re-mounted when switching from fetching to fetched. I think this could be a minor bug.
let wasAlreadyFetched = true;

/**
 * This component.
 *
 * NOTE: This component uses a key to force it to unmount and remount when the current board changes.
 *  This is recommended by the react team: https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html
 */
class CanvasElementOrchestrator extends React.PureComponent {
    initialRenderElementIds = [];
    orderedElementIds = [];

    state = {
        initialBoardRender: true,
        elementIdsToRender: EMPTY_ARRAY,
    };

    componentDidMount() {
        this.startBatchedRender();
    }

    componentWillReceiveProps(nextProps) {
        // Only handle changes during the initial board render
        if (!this.state.initialBoardRender) return;

        if (canvasElementIdsHaveChanged(this.props, nextProps)) {
            const addedElements = difference(nextProps.canvasElementIds, this.props.canvasElementIds);
            const removedElements = difference(this.props.canvasElementIds, nextProps.canvasElementIds);

            addedElements.forEach((elementId) => {
                this.initialRenderElementIds.push(elementId);
            });

            removedElements.forEach((elementId) => {
                let index = this.initialRenderElementIds.indexOf(elementId);
                if (index !== -1) {
                    this.initialRenderElementIds.splice(index, 1);
                }

                index = this.orderedElementIds.indexOf(elementId);
                if (index !== -1) {
                    this.orderedElementIds.splice(index, 1);
                }
            });
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    startBatchedRender = () => {
        // Calculate render order
        const result = calculateElementOrder(this.props, this.props.store);

        this.initialRenderElementIds = result.initialRenderElementIds;
        this.orderedElementIds = result.orderedElementIds;
        this.numberOfIterations = 0;

        this.scheduleBatchedRender();
    };

    scheduleBatchedRender = () => {
        if (this.unmounted) return;

        const { canvasElementIds } = this.props;
        const { elementIdsToRender } = this.state;

        this.numberOfIterations++;

        // First render
        if (this.numberOfIterations === 1) {
            if (this.props.isCurrentBoardFetched) {
                // Track navigation response time
                requestAnimationFrame(() => {
                    analyticsTimingService.endOperation(NewRelicPageActions.BOARD_NAVIGATION, {
                        wasAlreadyFetched,
                    });
                    wasAlreadyFetched = true;
                });
            } else {
                // Wait until the board is fetched and then capture the time
                wasAlreadyFetched = false;
            }

            this.setState({
                elementIdsToRender: this.initialRenderElementIds,
                initialBoardRender: this.initialRenderElementIds.length < canvasElementIds.length,
            });

            if (this.initialRenderElementIds.length < canvasElementIds.length) {
                rIC(this.scheduleBatchedRender);
            }

            return;
        }

        // Too many renders, bail out
        if (this.numberOfIterations > MAX_ITERATIONS) {
            this.setState({ initialBoardRender: false });
            return;
        }

        // Later renders
        const currentlyRenderedElementCount = elementIdsToRender.length;
        const totalElementCount = canvasElementIds.length;

        const newRenderCount = Math.min(currentlyRenderedElementCount + 5, totalElementCount);

        const finishedInitialRender = newRenderCount >= totalElementCount;

        if (finishedInitialRender) {
            this.setState({ initialBoardRender: false });
        } else {
            const newElementIdsToRender = [
                ...this.initialRenderElementIds,
                ...this.orderedElementIds.slice(0, newRenderCount - this.initialRenderElementIds.length),
            ];

            this.setState({
                elementIdsToRender: newElementIdsToRender,
            });

            // Schedule another render if it's not finished rendering
            rIC(this.scheduleBatchedRender);
        }
    };

    render() {
        const { initialBoardRender, elementIdsToRender } = this.state;

        const {
            canvasElementIds,
            sortIndexMap,
            gridSize,
            currentBoardId,
            permissions,
            deselectAll,
            createShortcutElement,
            createShortcutCard,
            createShortcutFile,
            createShortcutLink,
            createShortcutImageLink,
        } = this.props;

        const renderedElementIds = initialBoardRender ? elementIdsToRender : canvasElementIds;

        return (
            <React.Fragment>
                {renderedElementIds.map((elementId) => (
                    <CanvasElement
                        sortIndex={sortIndexMap[elementId]}
                        elementId={elementId}
                        key={elementId}
                        gridSize={gridSize}
                        currentBoardId={currentBoardId}
                        permissions={permissions}
                        deselectAll={deselectAll}
                        createShortcutElement={createShortcutElement}
                        createShortcutCard={createShortcutCard}
                        createShortcutFile={createShortcutFile}
                        createShortcutLink={createShortcutLink}
                        createShortcutImageLink={createShortcutImageLink}
                    />
                ))}
            </React.Fragment>
        );
    }
}

CanvasElementOrchestrator.propTypes = {
    canvasElementIds: PropTypes.array,
    sortIndexMap: PropTypes.object,
    gridSize: PropTypes.number,
    currentBoardId: PropTypes.string,
    permissions: PropTypes.number,
    isCurrentBoardFetched: PropTypes.bool,

    deselectAll: PropTypes.func,
    createShortcutElement: PropTypes.func,
    createShortcutCard: PropTypes.func,
    createShortcutFile: PropTypes.func,
    createShortcutLink: PropTypes.func,
    createShortcutImageLink: PropTypes.func,
    store: PropTypes.object.isRequired,
};

export default CanvasElementOrchestrator;
